/**
 * Color scale generation for brand skinning.
 *
 * The token system expects each brand color as a 50–900 ramp (light → dark),
 * mirroring the hand-tuned Tailwind-style ramps in `colors-foundation.js`.
 * A brand only needs to supply a single seed hex per role; this helper expands
 * it into a full ramp so a new customer skin does not have to hand-pick 10 stops.
 *
 * Two details matter for consistency with the rest of the theme:
 *  - The ramp goes 50 (lightest) → 900 (darkest), matching the existing scales.
 *  - The seed is placed at a configurable `anchor` index. The MUI theme reads
 *    the primary brand color from `key.blue[700]`, while accent controls
 *    (checkbox, toggle, tabs, links) read `key.gold[600]`. Anchoring the seed
 *    at the index a role actually consumes keeps the brand color exact where it
 *    is used, and derives the surrounding tints/shades around it.
 */

const SCALE_INDICES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];

/** Parse a #rrggbb / #rgb hex string into [r, g, b]. */
function hexToRgb(hex) {
  let s = hex.replace("#", "").trim();
  if (s.length === 3) {
    s = s
      .split("")
      .map((c) => c + c)
      .join("");
  }
  const int = parseInt(s, 16);
  return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
}

/** Format [r, g, b] back into a #rrggbb string. */
function rgbToHex([r, g, b]) {
  const clamp = (v) => Math.max(0, Math.min(255, Math.round(v)));
  return (
    "#" +
    [r, g, b]
      .map((v) => clamp(v).toString(16).padStart(2, "0"))
      .join("")
  );
}

/** Linearly mix `color` toward `target` by ratio (0 = color, 1 = target). */
function mix(color, target, ratio) {
  return color.map((c, i) => c + (target[i] - c) * ratio);
}

const WHITE = [255, 255, 255];
const BLACK = [0, 0, 0];

/**
 * Build a 50–900 color scale from a single seed hex.
 *
 * @param {string} seed - Seed hex (e.g. '#e11d48').
 * @param {object} [options]
 * @param {number} [options.anchor=700] - Scale index that equals the seed exactly.
 * @param {number} [options.lightMix=0.94] - Max mix toward white for the lightest stop.
 * @param {number} [options.darkMix=0.34] - Max mix toward black for the darkest stop.
 * @param {Record<number, string>} [options.overrides] - Explicit per-index hex overrides.
 * @returns {Record<number, string>} scale keyed by 50…900.
 */
export function generateScale(seed, options = {}) {
  const {
    anchor = 700,
    lightMix = 0.94,
    darkMix = 0.34,
    overrides = {},
  } = options;

  const seedRgb = hexToRgb(seed);
  const anchorPos = SCALE_INDICES.indexOf(anchor);
  const lastPos = SCALE_INDICES.length - 1;

  const scale = {};
  SCALE_INDICES.forEach((index, pos) => {
    if (overrides[index]) {
      scale[index] = overrides[index];
      return;
    }
    if (pos === anchorPos) {
      scale[index] = rgbToHex(seedRgb);
      return;
    }
    if (pos < anchorPos) {
      // Lighter than the seed: mix toward white, more the further away.
      const ratio = ((anchorPos - pos) / anchorPos) * lightMix;
      scale[index] = rgbToHex(mix(seedRgb, WHITE, ratio));
    } else {
      // Darker than the seed: mix toward black, more the further away.
      const ratio = ((pos - anchorPos) / (lastPos - anchorPos)) * darkMix;
      scale[index] = rgbToHex(mix(seedRgb, BLACK, ratio));
    }
  });
  return scale;
}

/**
 * Resolve a brand palette role definition into a concrete 50–900 scale.
 * Accepts either an explicit `{ scale: {...} }`, a `{ seed, anchor, overrides }`
 * spec, or a bare seed string.
 *
 * @param {string|{scale?:object, seed?:string, anchor?:number, overrides?:object}} def
 * @param {number} defaultAnchor - Anchor to use when the def does not specify one.
 */
export function resolveScale(def, defaultAnchor = 700) {
  if (!def) return null;
  if (typeof def === "string") {
    return generateScale(def, { anchor: defaultAnchor });
  }
  if (def.scale) {
    return def.scale;
  }
  return generateScale(def.seed, {
    anchor: def.anchor ?? defaultAnchor,
    overrides: def.overrides,
    ...(def.lightMix != null ? { lightMix: def.lightMix } : {}),
    ...(def.darkMix != null ? { darkMix: def.darkMix } : {}),
  });
}

export default generateScale;
